import { Col, Row } from "antd";
import dayjs from "dayjs";
import { get } from "lodash";
import { useTranslation } from "react-i18next";
import {
  MdOutlineBadge,
  MdOutlineCake,
  MdOutlineFamilyRestroom,
  MdOutlineSubtitles,
  MdPeopleOutline,
} from "react-icons/md";
import Form from "shared/components/Form";
import FormField from "shared/components/FormField";
import Input from "shared/components/Input";
import Select from "shared/components/Select";
import { NAME_SPACES } from "shared/locales/constants";
import {
  DATE_FORMAT,
  FAMILY_SITUATION,
  FAMILY_SITUATIONS,
  GENDER,
  SUBSCRIPTION_FORM_KEYS,
} from "utils/constants";

const Adherent = ({
  setPercent,
  isFieldRequired,
  initialValues,
  bindField,
  config,
}) => {
  const { t } = useTranslation(NAME_SPACES.PRIVATE.BROKER.CONTRACT);
  const SUBSCRIPTION = t("SUBSCRIPTION", { returnObjects: true });

  const genderPath = "project.contact.user.gender";
  const genderField = bindField(genderPath, {
    label: SUBSCRIPTION.FORM.GENDER.LABEL,
    required: isFieldRequired(genderPath),
    onChange: () => setPercent(initialValues),
  });

  const lastnamePath = "project.contact.user.lastname";
  const lastnameField = bindField(lastnamePath, {
    label: SUBSCRIPTION.FORM.LAST_NAME.LABEL,
    required: isFieldRequired(lastnamePath),
    disabled: true,
  });

  const birthNamePath = "project.contact.user.birthName";
  const birthNameField = bindField(birthNamePath, {
    label: SUBSCRIPTION.FORM.BIRTH_NAME.LABEL,
    required: isFieldRequired(birthNamePath),
    onBlur: () => setPercent(initialValues),
  });

  const firstnamePath = "project.contact.user.firstname";
  const firstnameField = bindField(firstnamePath, {
    label: SUBSCRIPTION.FORM.FIRST_NAME.LABEL,
    required: isFieldRequired(firstnamePath),
    disabled: true,
  });

  const birthDatePath = "project.contact.user.birthDate";
  const birthDateField = bindField(birthDatePath, {
    label: SUBSCRIPTION.FORM.BIRTH_DATE.LABEL,
    required: isFieldRequired(birthDatePath),
    disabled: true,
    onBlur: () => setPercent(initialValues),
  });

  const socialSecurityNumberPath = "project.contact.socialSecurityNumber";
  const socialSecurityNumberField = bindField(socialSecurityNumberPath, {
    label: SUBSCRIPTION.FORM.SOCIAL_SECURITY_NUMBER.LABEL,
    required: isFieldRequired(socialSecurityNumberPath),
    onBlur: () => setPercent(initialValues),
  });

  const familySituationPath = "project.contact.fields.familySituation";
  const familySituationField = bindField(familySituationPath, {
    label: SUBSCRIPTION.FORM.FAMILY_SITUATION.LABEL,
    required: isFieldRequired(familySituationPath),
    onChange: () => setPercent(initialValues),
  });

  return (
    <div
      className="subscription-form--wrapper"
      id={`${SUBSCRIPTION_FORM_KEYS.ADDITIONAL_INFORMATION.ADHERENT}-section`}
    >
      <Form
        type="vertical"
        title={SUBSCRIPTION.FORM.ADDITIONAL_INFORMATION.ADHERENT.TITLE}
        subtitle={SUBSCRIPTION.FORM.ADDITIONAL_INFORMATION.ADHERENT.SUBTITLE}
      >
        <Row gutter={[16, 16]}>
          <Col lg={24} xs={24}>
            <FormField
              path={genderField.path}
              label={genderField.label}
              required={genderField.required}
              disabled={genderField.disabled}
            >
              {(field) => (
                <Input
                  type="radio"
                  variant="group"
                  values={[
                    {
                      value: GENDER.MALE,
                      label: SUBSCRIPTION.FORM.GENDER.OPTIONS.MR,
                    },
                    {
                      value: GENDER.FEMALE,
                      label: SUBSCRIPTION.FORM.GENDER.OPTIONS.MRS,
                    },
                  ]}
                  icon={<MdPeopleOutline icon="man" size={16} />}
                  label={genderField.label}
                  value={get(initialValues, genderPath)}
                  {...field}
                  onChange={genderField.onChange}
                  onBlur={genderField.onBlur}
                />
              )}
            </FormField>
          </Col>
          <Col lg={24} xs={24}>
            <FormField
              path={lastnameField.path}
              label={lastnameField.label}
              required={lastnameField.required}
              disabled={lastnameField.disabled}
            >
              {(field) => (
                <Input
                  placeholder={SUBSCRIPTION.FORM.LAST_NAME.PLACEHOLDER}
                  label={lastnameField.label}
                  icon={<MdOutlineBadge size={16} />}
                  defaultValue={lastnameField.defaultValue}
                  {...field}
                  onChange={lastnameField.onChange}
                  onBlur={lastnameField.onBlur}
                />
              )}
            </FormField>
          </Col>
          <Col lg={24} xs={24}>
            <FormField
              path={birthNameField.path}
              label={birthNameField.label}
              required={birthNameField.required}
              disabled={birthNameField.disabled}
            >
              {(field) => (
                <Input
                  placeholder={SUBSCRIPTION.FORM.BIRTH_NAME.PLACEHOLDER}
                  label={birthNameField.label}
                  icon={<MdOutlineBadge size={16} />}
                  type="custom"
                  normalizeText={true}
                  defaultValue={birthNameField.defaultValue}
                  {...field}
                  onChange={birthNameField.onChange}
                  onBlur={birthNameField.onBlur}
                  optional={!birthNameField.required}
                />
              )}
            </FormField>
          </Col>
          <Col lg={24} xs={24}>
            <FormField
              path={firstnameField.path}
              label={firstnameField.label}
              required={firstnameField.required}
              disabled={firstnameField.disabled}
            >
              {(field) => (
                <Input
                  label={firstnameField.label}
                  icon={<MdOutlineBadge size={16} />}
                  placeholder={SUBSCRIPTION.FORM.FIRST_NAME.PLACEHOLDER}
                  defaultValue={firstnameField.defaultValue}
                  {...field}
                  onChange={firstnameField.onChange}
                  onBlur={firstnameField.onBlur}
                />
              )}
            </FormField>
          </Col>
          <Col lg={24} xs={24}>
            <FormField
              path={birthDateField.path}
              label={birthDateField.label}
              required={birthDateField.required}
              disabled={birthDateField.disabled}
            >
              {(field) => (
                <Input
                  placeholder={SUBSCRIPTION.FORM.BIRTH_DATE.PLACEHOLDER}
                  type="date"
                  variant="simple"
                  label={birthDateField.label}
                  defaultValue={
                    get(initialValues, birthDatePath) &&
                    dayjs(get(initialValues, birthDatePath))
                  }
                  icon={<MdOutlineCake size={16} />}
                  format={DATE_FORMAT}
                  {...field}
                  onChange={birthDateField.onChange}
                  onBlur={birthDateField.onBlur}
                  optional={!birthDateField.required}
                />
              )}
            </FormField>
          </Col>
          <Col lg={24} xs={24}>
            <FormField
              path={socialSecurityNumberField.path}
              label={socialSecurityNumberField.label}
              required={socialSecurityNumberField.required}
              disabled={socialSecurityNumberField.disabled}
            >
              {(field) => (
                <Input
                  label={socialSecurityNumberField.label}
                  placeholder={
                    SUBSCRIPTION.FORM.SOCIAL_SECURITY_NUMBER.PLACEHOLDER
                  }
                  icon={<MdOutlineSubtitles size={16} />}
                  type="custom"
                  trim={true}
                  defaultValue={socialSecurityNumberField.defaultValue}
                  {...field}
                  onChange={socialSecurityNumberField.onChange}
                  onBlur={socialSecurityNumberField.onBlur}
                  optional={!socialSecurityNumberField.required}
                />
              )}
            </FormField>
          </Col>
          <Col lg={24} xs={24}>
            <FormField
              path={familySituationField.path}
              label={familySituationField.label}
              required={familySituationField.required}
              disabled={familySituationField.disabled}
            >
              {(field) => (
                <Select
                  label={familySituationField.label}
                  optional={SUBSCRIPTION.FORM.OPTIONAL}
                  icon={<MdOutlineFamilyRestroom size={16} />}
                  placeholder={SUBSCRIPTION.FORM.FAMILY_SITUATION.PLACEHOLDER}
                  type="simple"
                  options={Object.entries(
                    get(
                      FAMILY_SITUATIONS,
                      get(config, "envelop"),
                      FAMILY_SITUATION,
                    ),
                  ).map(([key, value]) => ({
                    value,
                    label: get(
                      SUBSCRIPTION.FORM.FAMILY_SITUATION,
                      `${get(config, "envelop")}.OPTIONS.${key}`,
                      SUBSCRIPTION.FORM.FAMILY_SITUATION.OPTIONS[key],
                    ),
                  }))}
                  defaultValue={familySituationField.defaultValue}
                  {...field}
                  onChange={familySituationField.onChange}
                  onBlur={familySituationField.onBlur}
                />
              )}
            </FormField>
          </Col>
        </Row>
      </Form>
    </div>
  );
};

export default Adherent;
